/* eslint-disable consistent-return */
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { ThemeProvider } from 'styled-components';
import { useQuery } from '@apollo/react-hooks';
import PropTypes from 'prop-types';
import Layout from '../common/Layout/Layout';
import classAssignment from '../../apollo/queries/classAssignment.graphql';
import teamsPerClass from '../../apollo/queries/teamsPerClass.graphql';
import Spinner from '../common/Spinner/Spinner';
import { getLangCode } from '../../utils/language';
import { useErrorHandler } from '../common/ErrorHandler/ErrorHandler';
import AssignmentContext from '../common/AssignmentContext/AssignmentContext';
import Select from '../common/Select/Select';
import assignmentTypes from '../../utils/assignmentTypes';
import { isNcleAlefAssignment } from '../../utils/isNcleAlefAssignment';
import AssignmentStudentsList from './AssignmentStudentsList';
import {
  studentListFilters,
  LOCKED_FILTER_KEY,
  getUnassignedFilterKey,
} from './studentsListFilter';
import { selectedKeyToInputFilter } from './helpers';
import NewAssignmentClassAnalytics from './NewAssignmentClassAnalytics/NewAssignmentClassAnalytics';

const AssignmentClassDashboard = ({
  teacherAssignmentParams,
  alefAssignmentParams,
  isGradableItemsLockedByDefault,
  assignmentType,
  onGoBack,
  onNavigateToStudent,
  onSubmissionsReturn,
  finishExperienceContext,
  classId,
  groupId,
  organisationName,
  trimesterOrder,
  displayHeader,
  displaySubjectColor,
  isNcle,
}) => {
  const { t, dir, i18n } = useTranslation();
  const {
    assignmentInstanceId,
    mloId: lessonId,
    instructionalPlanId: ipId,
    academicYearId: acYearId,
    contentId: cId,
    contentType: cType,
  } = teacherAssignmentParams || {};
  const {
    assignmentId,
    mloId,
    instructionalPlanId,
    contentId,
    academicYearId,
    contentType,
  } = alefAssignmentParams || {};

  const { handleError } = useErrorHandler();
  const handleErrorCallback = useCallback(e => {
    handleError(e);
  }, []);

  const [selectedFilter, setselectedFilter] = useState(
    () => studentListFilters[0],
  );
  const [selectedTeamFilter, setselectedTeamFilter] = useState('');
  const input = {
    type: assignmentType,
    assignmentId,
    referrerId: assignmentInstanceId || mloId,
    classId,
    lessonId: assignmentType === assignmentTypes.TEACHER ? lessonId : undefined,
    contentId: contentId || cId,
    filter: selectedKeyToInputFilter(selectedFilter.key),
    isGradableItemsLockedByDefault,
  };

  const {
    data: assingmentData = null,
    loading: assignmentLoading,
    refetch: refetchClassAssignmentList,
  } = useQuery(classAssignment, {
    variables: {
      input,
    },
    onError: handleErrorCallback,
    fetchPolicy: 'network-only',
  });

  const {
    data: assigmentTeamsData = {
      teamsPerClass: {
        teams: [],
      },
    },
    loading: assignmentTeamsLoading,
  } = useQuery(teamsPerClass, {
    variables: {
      classId,
    },
    onError: handleErrorCallback,
    fetchPolicy: 'network-only',
  });

  const isLoading = assignmentTeamsLoading || assignmentLoading;

  if (
    (assignmentLoading && !assingmentData) ||
    (assignmentTeamsLoading && !assigmentTeamsData)
  ) {
    return <Spinner isCentered={true} testId="assignmentSpinner" />;
  }

  const classAssignmentData = assingmentData && assingmentData.classAssignment;
  if (!classAssignmentData || !assigmentTeamsData) {
    handleErrorCallback();
    return <></>;
  }

  const {
    mloName,
    subjectCategory,
    title,
    dueOn,
    maxScore,
    maxStars,
    description,
    attachment,
    language,
    gradeable,
    attachmentRequired,
    commentRequired,
  } = classAssignmentData.assignment.assignmentInstance;

  const renderListFilters = (
    <FilterList>
      <Select
        testId="class-assignment-select-filter"
        selectionChanged={selection => {
          localStorage.setItem(
            'dashboardFilterSelection',
            JSON.stringify(selection),
          );
          setselectedFilter(selection);
        }}
        textKey={'key'}
        menuItems={studentListFilters
          .map(
            f =>
              f.key === LOCKED_FILTER_KEY
                ? {
                    ...f,
                    key: getUnassignedFilterKey(
                      isNcleAlefAssignment(isNcle, assignmentType),
                    ),
                  }
                : f,
          )
          .filter(f => {
            switch (f.key) {
              case 'Late Submissions':
                return (
                  assignmentType === assignmentTypes.TEACHER &&
                  (attachmentRequired || commentRequired)
                );
              case 'Left the Class':
                return assignmentType === assignmentTypes.TEACHER;
              case 'Pending Submission':
                return attachmentRequired || commentRequired;
              default:
                return true;
            }
          })}
        selectedItem={selectedFilter}
        lang={i18n.language}
      />
    </FilterList>
  );

  const goBackToPreviousPage = () => {
    onGoBack();
  };

  const selectedTeamMembersIds =
    selectedTeamFilter === ''
      ? []
      : assigmentTeamsData.teamsPerClass.teams
          .find(team => team.id === selectedTeamFilter)
          .members.map(m => m.learnerId);

  return (
    <AssignmentContext.Provider
      value={{
        assignmentParams: {
          assignmentId:
            classAssignmentData.assignment.assignmentInstance.assignmentId,
          assignmentInstanceId,
          classId,
          mloId: mloId || lessonId,
          instructionalPlanId: instructionalPlanId || ipId,
          contentId: contentId || cId,
          academicYearId: academicYearId || acYearId,
          contentType: contentType || cType,
          isGradableItemsLockedByDefault,
          groupId,
          organisationName,
          trimesterOrder,
          isNcle,
          finishExperienceContext,
        },
        assignmentData: {
          assignmentType,
          assignmentId:
            classAssignmentData.assignment.assignmentInstance.assignmentId,
          totalStudentsAssigned: selectedTeamFilter
            ? selectedTeamMembersIds.length
            : classAssignmentData.assignment.assignmentStats
                .assignedStudentsCount,
          isLoadingResults: isLoading,
        },
        filters: {
          selectedFilter,
          renderListFilters,
          selectedTeamFilter,
          setselectedTeamFilter,
          selectedTeamMembersIds,
        },
        methods: { refetchClassAssignmentList },
      }}
    >
      <ThemeProvider
        theme={{ dir, lang: language, langCode: getLangCode(language) }}
      >
        <Layout
          backButtonText={t('Back to Lesson')}
          title={mloName}
          subjectCategory={subjectCategory}
          onGoBack={goBackToPreviousPage}
          displayHeader={displayHeader}
          displaySubjectColor={displaySubjectColor}
        >
          <NewAssignmentClassAnalytics
            assignmentTitle={title}
            dueOn={dueOn}
            maxScore={maxScore}
            stats={classAssignmentData.assignment.assignmentStats}
            unassignedStudentCount={
              classAssignmentData.unAssignedStudents.length
            }
            description={description}
            attachment={attachment}
            gradeable={gradeable}
            attachmentRequired={attachmentRequired}
            commentRequired={commentRequired}
            assignmentType={assignmentType}
            isNcle={isNcle}
          />
          <AssignmentStudentsList
            groupedSubmissions={classAssignmentData.groupedSubmissions}
            unAssignedStudents={classAssignmentData.unAssignedStudents}
            dueOn={dueOn}
            maxScore={maxScore}
            maxStars={maxStars}
            gradeable={gradeable}
            attachmentRequired={attachmentRequired}
            commentRequired={commentRequired}
            onNavigateToStudent={onNavigateToStudent}
            onSubmissionsReturn={returnedSubmissions => {
              // Tell the host whether the server already finished the experiences
              // (ALEF-93947), so it only skips its own call when this bundle sent finishContext.
              onSubmissionsReturn(returnedSubmissions, maxScore, {
                finishedServerSide: Boolean(finishExperienceContext),
              });
            }}
            listFilters={renderListFilters}
            teamsData={assigmentTeamsData.teamsPerClass.teams}
          />
        </Layout>
      </ThemeProvider>
    </AssignmentContext.Provider>
  );
};

AssignmentClassDashboard.defaultProps = {
  teacherAssignmentParams: null,
  alefAssignmentParams: null,
  onGoBack: () => {},
  onNavigateToStudent: () => {},
  onSubmissionsReturn: () => {},
  displayHeader: true,
  displaySubjectColor: true,
  isNcle: false,
  finishExperienceContext: null,
};
AssignmentClassDashboard.propTypes = {
  teacherAssignmentParams: PropTypes.shape({
    assignmentInstanceId: PropTypes.string,
    mloId: PropTypes.string,
    instructionalPlanId: PropTypes.string,
    contentId: PropTypes.string,
    academicYearId: PropTypes.string,
    contentType: PropTypes.string,
  }),
  alefAssignmentParams: PropTypes.shape({
    assignmentId: PropTypes.string,
    mloId: PropTypes.string,
    instructionalPlanId: PropTypes.string,
    contentId: PropTypes.string,
    academicYearId: PropTypes.string,
    contentType: PropTypes.string,
  }),
  isGradableItemsLockedByDefault: PropTypes.bool.isRequired,
  assignmentType: PropTypes.string.isRequired,
  onGoBack: PropTypes.func,
  onNavigateToStudent: PropTypes.func,
  onSubmissionsReturn: PropTypes.func,
  finishExperienceContext: PropTypes.object,
  classId: PropTypes.string.isRequired,
  groupId: PropTypes.string.isRequired,
  organisationName: PropTypes.string.isRequired,
  trimesterOrder: PropTypes.number.isRequired,
  displayHeader: PropTypes.bool,
  displaySubjectColor: PropTypes.bool,
  isNcle: PropTypes.bool,
};

const FilterList = styled.div`
  margin-bottom: 20px;
`;

export default AssignmentClassDashboard;
